import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { UploadCloud, ShieldAlert, Loader2, CheckCircle2, Wand2, X } from "lucide-react";

export default function OpsKpiMasterImport() {
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [token, setToken] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const handleImport = async () => {
    if (!file) { toast.error("Sube el archivo JSON del catálogo maestro"); return; }
    if (!token.trim()) { toast.error("Falta el X-Migration-Token"); return; }
    setImporting(true);
    setError("");
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await api.post("/ops/admin/kpis/bulk-import-master", formData, {
        headers: { "Content-Type": "multipart/form-data", "X-Migration-Token": token.trim() },
      });
      setResult(res.data);
      toast.success("Catálogo maestro cargado correctamente");
    } catch (e) {
      const detail = e.response?.data?.detail || "Error al cargar el catálogo";
      setError(detail);
      toast.error(detail);
    } finally {
      setImporting(false);
      setConfirming(false);
    }
  };

  if (result) {
    return (
      <div className="p-4 sm:p-6 max-w-lg mx-auto space-y-5">
        <div className="bg-white rounded-2xl border border-emerald-200 p-6 text-center space-y-3" data-testid="kpi-import-result">
          <CheckCircle2 className="w-14 h-14 text-emerald-500 mx-auto" />
          <h1 className="text-xl font-bold text-slate-900">Catálogo maestro cargado</h1>
          <div className="grid grid-cols-2 gap-3 text-left text-sm bg-slate-50 rounded-xl p-4">
            <div><span className="text-slate-500">Categorías</span><div className="font-bold text-lg">{result.categories_created}</div></div>
            <div><span className="text-slate-500">KPIs creados</span><div className="font-bold text-lg">{result.kpis_created}</div></div>
            <div><span className="text-slate-500">KPIs archivados (legado)</span><div className="font-bold text-lg">{result.kpis_archived}</div></div>
            <div><span className="text-slate-500">Con fórmula lista</span><div className="font-bold text-lg">{result.kpis_with_formula}</div></div>
            <div className="col-span-2"><span className="text-slate-500">Pendientes de configurar</span><div className="font-bold text-lg text-yellow-600">{result.kpis_pending_configuration}</div></div>
          </div>
          <button
            onClick={() => navigate("/ops/admin/kpis/wizard-configuracion")}
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-[#1A1A1A] text-white font-semibold hover:bg-black transition-colors"
            data-testid="kpi-import-goto-wizard-btn"
          >
            <Wand2 className="w-4 h-4" /> Continuar al wizard de configuración
          </button>
          <button onClick={() => navigate("/ops/admin/kpis/catalogo")} className="w-full py-2.5 text-sm text-slate-500 hover:text-slate-800">
            Ver catálogo
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 max-w-lg mx-auto space-y-5">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Cargar catálogo maestro de KPIs</h1>
        <p className="text-sm text-slate-500">Carga única. Después de esta acción el catálogo queda bloqueado.</p>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800 space-y-1.5" data-testid="kpi-import-instructions">
        <p className="font-semibold">Antes de continuar:</p>
        <ul className="list-disc pl-5 space-y-0.5">
          <li>Esta acción archiva los KPIs actuales del sistema (quedan como legado, no se borran).</li>
          <li>Crea las 10 categorías oficiales y sus KPIs (fórmula, meta y ponderación ya definidas por Olimpo).</li>
          <li>El catálogo queda bloqueado: nadie puede crear ni borrar KPIs salvo el superadmin de Olimpo.</li>
          <li>Solo se puede ejecutar una vez. Necesitas el <strong>X-Migration-Token</strong> de este entorno.</li>
        </ul>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700 flex items-start gap-2" data-testid="kpi-import-error">
          <ShieldAlert className="w-4 h-4 mt-0.5 shrink-0" /> {error}
        </div>
      )}

      <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
        <div>
          <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">Archivo JSON del catálogo</label>
          <label className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-slate-200 rounded-xl py-8 cursor-pointer hover:border-[#FFDF00] transition-colors">
            <UploadCloud className="w-8 h-8 text-slate-400" />
            <span className="text-sm text-slate-500">{file ? file.name : "Seleccionar archivo .json"}</span>
            <input type="file" accept=".json,application/json" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="kpi-import-file-input" />
          </label>
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">X-Migration-Token</label>
          <input
            type="password"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            placeholder="Token de migración de este entorno"
            className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#FFDF00]"
            data-testid="kpi-import-token-input"
          />
        </div>
        <button
          onClick={() => setConfirming(true)}
          disabled={!file || !token.trim() || importing}
          className="w-full py-3.5 rounded-xl bg-red-600 text-white font-bold hover:bg-red-700 transition-colors disabled:opacity-40"
          data-testid="kpi-import-execute-btn"
        >
          Ejecutar carga
        </button>
      </div>

      {confirming && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md p-6 space-y-4 shadow-2xl">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-bold text-slate-900">¿Confirmas la carga?</h3>
              <button onClick={() => setConfirming(false)} className="p-1.5 rounded-lg hover:bg-slate-100"><X className="w-5 h-5" /></button>
            </div>
            <p className="text-sm text-slate-600">
              Esta acción archivará los KPIs actuales y creará los KPIs oficiales del catálogo maestro.
              <strong> No se puede deshacer</strong> sin contactar a Olimpo.
            </p>
            <div className="flex gap-3 pt-2">
              <button onClick={() => setConfirming(false)} className="flex-1 py-2.5 rounded-xl border border-slate-200 font-semibold text-slate-600 hover:bg-slate-50">Cancelar</button>
              <button
                onClick={handleImport}
                disabled={importing}
                className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl bg-red-600 text-white font-bold hover:bg-red-700 disabled:opacity-50"
                data-testid="kpi-import-confirm-btn"
              >
                {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Sí, continuar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
